import { useState, useMemo, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Input,
  Control,
  Label,
  Tabs,
  Tab,
  Toggle,
  Divider,
  Button,
  Grid,
  Cell,
  Select,
  SelectOption,
  Badge,
  TrashIcon,
  SearchIcon,
  PlusIcon,
  ClockIcon,
  GroupIcon,
  useDocumentTitle,
  SuccessIcon,
} from '@forge/common';
import mockData from '@/data/mock-data.json';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useMyGroups } from '@/hooks/useMyGroups';
import { updateProfile, updateMfaEmail, verifyMfa } from '@/api/profile';
import type { UpdateProfilePayload, UserProfile } from '@/api/profile';
import { requestToJoinGroup, cancelJoinRequest } from '@/api/groups';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';

const countryFromCode = (code: string): string =>
  mockData.countryCodes.find((c) => c.code === code)?.country ?? '';

const ProfilePage = () => {
  useDocumentTitle('Profile — OneRing Portal');

  // API-backed data
  const { user, isLoading: isUserLoading, isUsingMockData: isUserMock, reload: reloadUser } = useCurrentUser();

  const isUsingMockData = isUserMock;

  const [activeTab, setActiveTab] = useState('profile');

  // Profile form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // MFA state
  const [mfaEmail, setMfaEmail] = useState('');
  const [mfaVerified, setMfaVerified] = useState(false);

  // Notification preferences
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyBrowser, setNotifyBrowser] = useState(true);
  const [notifyIncidents, setNotifyIncidents] = useState(false);
  const [notifyWeekly, setNotifyWeekly] = useState(true);

  // Groups state (real data via API)
  const [groupSearch, setGroupSearch] = useState('');
  const {
    groups,
    isLoading: isGroupsLoading,
    reload: reloadGroups,
  } = useMyGroups();

  // Sync form state when user data arrives
  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setCountry(countryFromCode(user.countryCode ?? ''));
    setPhoneNumber(user.phone ?? '');
    setMfaEmail(user.mfaEmail ?? '');
    setMfaVerified(user.mfaVerified);
  }, [user]);

  // Country code is stored as a dial code (e.g. "+1"); the Select tracks the
  // unique country name and we derive the dial code for diffing/saving.
  const countryCode = useMemo(
    () => mockData.countryCodes.find((c) => c.country === country)?.code ?? '',
    [country],
  );

  // Mutations
  const updateProfileMutation = useApiMutation<UpdateProfilePayload, UserProfile>({
    mutationFn: (data) => updateProfile(data),
    successMessage: 'Profile updated successfully',
    errorMessage: 'Failed to update profile',
    onSuccess: () => reloadUser(),
  });

  const updateMfaMutation = useApiMutation<string, UserProfile>({
    mutationFn: (newMfaEmail) => updateMfaEmail(newMfaEmail),
    successMessage: 'MFA email updated successfully',
    errorMessage: 'Failed to update MFA email',
    onSuccess: () => reloadUser(),
  });

  const verifyMfaMutation = useApiMutation<void, UserProfile>({
    mutationFn: () => verifyMfa(),
    successMessage: 'MFA verification sent',
    errorMessage: 'Failed to verify MFA',
    onSuccess: (result) => {
      setMfaVerified(result.mfaVerified);
      reloadUser();
    },
  });

  // Group join request mutations
  const requestJoinMutation = useApiMutation<string, unknown>({
    mutationFn: (groupId) => requestToJoinGroup(groupId),
    successMessage: 'Request submitted for approval',
    errorMessage: 'Failed to submit request',
    onSuccess: () => reloadGroups(),
  });

  const cancelRequestMutation = useApiMutation<string, unknown>({
    mutationFn: (requestId) => cancelJoinRequest(requestId),
    successMessage: 'Request withdrawn',
    errorMessage: 'Failed to withdraw request',
    onSuccess: () => reloadGroups(),
  });

  // Groups the user already belongs to (Keycloak is the source of truth)
  const userGroups = groups.memberOf;
  const pendingRequests = groups.pendingRequests;

  // Available groups, filtered by the search box
  const availableGroups = useMemo(() => {
    const q = groupSearch.toLowerCase();
    return groups.available.filter(
      (g) =>
        !q ||
        g.name.toLowerCase().includes(q) ||
        (g.description ?? '').toLowerCase().includes(q),
    );
  }, [groups.available, groupSearch]);

  const userInitials =
    `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();

  const profileChanged = useMemo(() => {
    if (!user) return false;
    return (
      firstName !== user.firstName ||
      lastName !== user.lastName ||
      email !== user.email ||
      countryCode !== (user.countryCode ?? '') ||
      phoneNumber !== (user.phone ?? '')
    );
  }, [firstName, lastName, email, countryCode, phoneNumber, user]);

  const mfaChanged = useMemo(() => {
    if (!user) return false;
    return mfaEmail !== (user.mfaEmail ?? '');
  }, [mfaEmail, user]);

  const handleReset = () => {
    if (!user) return;
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setCountry(countryFromCode(user.countryCode ?? ''));
    setPhoneNumber(user.phone ?? '');
  };

  const handleSaveProfile = () => {
    updateProfileMutation.mutate({
      firstName,
      lastName,
      email,
      phone: phoneNumber,
      countryCode,
    });
  };

  const handleSaveMfaEmail = () => {
    updateMfaMutation.mutate(mfaEmail);
  };

  const handleVerifyMfa = () => {
    verifyMfaMutation.mutate(undefined as never);
  };

  const handleDeleteMfa = () => {
    setMfaEmail('');
    setMfaVerified(false);
  };

  const handleRequestGroup = (groupId: string) => {
    requestJoinMutation.mutate(groupId);
  };

  const handleCancelRequest = (requestId: string) => {
    cancelRequestMutation.mutate(requestId);
  };

  if (isUserLoading) {
    return (
      <Grid cols={12}>
        <Cell colSpan={10} col={2}>
          <div className="flex items-center justify-center py-12">
            <p className="text-sm text-color-tertiary">Loading profile...</p>
          </div>
        </Cell>
      </Grid>
    );
  }

  return (
    <Grid cols={12}>
      <Cell colSpan={10} col={2}>
        {isUsingMockData && <MockDataBanner featureName="Profile" />}

        <Tabs value={activeTab} onChange={(id) => setActiveTab(String(id))}>
          <Tab id="profile" active={activeTab === 'profile'}>
            Profile
          </Tab>
          <Tab id="groups" active={activeTab === 'groups'}>
            Groups
          </Tab>
          <Tab id="notifications" active={activeTab === 'notifications'}>
            Notifications
          </Tab>
        </Tabs>

        {activeTab === 'profile' && (
          <div className="mt-3">
            {/* Profile Overview Card */}
            <Card>
            <CardHeader title="Overview" />
            <CardContent top="none">
              <div className="flex flex-col items-center mb-6">
                <Avatar text={userInitials} size="xl" />
              </div>
              <form
                className="flex flex-col gap-2"
                onSubmit={(e) => e.preventDefault()}
              >
                <Grid cols={12}>
                  <Cell colSpan={12} md={{ colSpan: 6 }}>
                    <Control>
                      <Label htmlFor="firstName" required requiredText="">
                        First Name
                      </Label>
                      <Input
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </Control>
                  </Cell>
                  <Cell colSpan={12} md={{ colSpan: 6 }}>
                    <Control>
                      <Label htmlFor="lastName" required requiredText="">
                        Last Name
                      </Label>
                      <Input
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </Control>
                  </Cell>
                </Grid>

                <Control>
                  <Label htmlFor="email" required requiredText="">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Control>

                <Grid cols={12}>
                  <Cell colSpan={12} md={{ colSpan: 4 }}>
                    <Control>
                      <Label htmlFor="countryCode">Country Code</Label>
                      <Select
                        id="countryCode"
                        placeholder="Select Country Code"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      >
                        {mockData.countryCodes.map((cc) => (
                          <SelectOption
                            key={`${cc.country}-${cc.code}`}
                            value={cc.country}
                            label={`${cc.country} (${cc.code})`}
                          >
                            {`${cc.country} (${cc.code})`}
                          </SelectOption>
                        ))}
                      </Select>
                    </Control>
                  </Cell>
                  <Cell colSpan={12} md={{ colSpan: 8 }}>
                    <Control>
                      <Label htmlFor="phoneNumber" required requiredText="">
                        Phone Number
                      </Label>
                      <Input
                        id="phoneNumber"
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        required
                      />
                    </Control>
                  </Cell>
                </Grid>

                <Control>
                  <Label htmlFor="username" required requiredText="">
                    Username
                  </Label>
                  <Input id="username" value={user?.username ?? ''} disabled />
                </Control>

                <div className="flex justify-between pt-2">
                  <Button
                    variant="quiet-primary"
                    disabled={!profileChanged || updateProfileMutation.isSubmitting}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!profileChanged || updateProfileMutation.isSubmitting}
                    onClick={handleSaveProfile}
                  >
                    {updateProfileMutation.isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* MFA Card */}
          <Card className="mt-6">
            <CardHeader title="Multi-Factor Authentication" />
            <CardContent top="none">
              <Label htmlFor="mfaEmail">Email (Default)</Label>
              <Grid cols={12}>
                <Cell colSpan={12} md={{ colSpan: 10 }}>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        id="mfaEmail"
                        type="email"
                        value={mfaEmail}
                        onChange={(e) => {
                          setMfaEmail(e.target.value);
                          setMfaVerified(false);
                        }}
                        suffix={
                          mfaVerified ? (
                            <SuccessIcon className="text-green-600" />
                          ) : undefined
                        }
                      />
                    </div>
                    <Button
                      variant={mfaVerified ? 'secondary' : 'primary'}
                      disabled={mfaVerified || !mfaEmail || mfaChanged || verifyMfaMutation.isSubmitting}
                      onClick={handleVerifyMfa}
                    >
                      {verifyMfaMutation.isSubmitting ? 'Verifying...' : mfaVerified ? 'Verified' : 'Verify'}
                    </Button>
                    <Button
                      variant="destructive-secondary"
                      icon={<TrashIcon />}
                      aria-label="Remove MFA email"
                      onClick={handleDeleteMfa}
                    />
                  </div>
                </Cell>
              </Grid>
              {mfaChanged && (
                <div className="flex justify-end mt-3">
                  <Button
                    variant="primary"
                    disabled={!mfaEmail || updateMfaMutation.isSubmitting}
                    onClick={handleSaveMfaEmail}
                  >
                    {updateMfaMutation.isSubmitting ? 'Saving...' : 'Update MFA Email'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'groups' && (
          <div className="mt-3 flex flex-col gap-6">
            {isGroupsLoading ? (
              <Card>
                <CardContent>
                  <p className="text-sm text-color-tertiary text-center py-6">
                    Loading your groups…
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* My Groups Card */}
                <Card>
                  <CardHeader
                    title="My Groups"
                    subtitle={`You are a member of ${userGroups.length} group${userGroups.length !== 1 ? 's' : ''}`}
                  />
                  <CardContent top="none">
                    {userGroups.length === 0 ? (
                      <p className="text-sm text-color-tertiary">
                        You are not currently a member of any groups.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {userGroups.map((group) => (
                          <div
                            key={group.id}
                            className="flex flex-col gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 shrink-0">
                                  <GroupIcon className="w-4 h-4" />
                                </span>
                                <p className="font-semibold text-sm truncate">
                                  {group.name}
                                </p>
                              </div>
                              <Badge color="green">Member</Badge>
                            </div>
                            {group.description && (
                              <p className="text-xs text-color-secondary dark:text-color-secondary-dark line-clamp-2">
                                {group.description}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Pending Requests Card */}
                {pendingRequests.length > 0 && (
                  <Card>
                    <CardHeader
                      title="Pending Requests"
                      subtitle="Your group membership requests awaiting approval"
                    />
                    <CardContent top="none">
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {pendingRequests.map((request) => (
                          <div
                            key={request.id}
                            className="flex flex-col gap-3 p-4 rounded-xl border border-orange-200 dark:border-orange-700/60 bg-orange-50 dark:bg-orange-900/20"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <ClockIcon className="w-4 h-4 text-orange-500 shrink-0" />
                                <span className="font-semibold text-sm truncate">
                                  {request.groupName}
                                </span>
                              </div>
                              <Badge color="orange">Pending</Badge>
                            </div>
                            <Button
                              variant="tertiary"
                              className="mt-auto"
                              disabled={cancelRequestMutation.isSubmitting}
                              onClick={() => handleCancelRequest(request.id)}
                            >
                              Cancel request
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Request to Join Card */}
                <Card>
                  <CardHeader
                    title="Request to Join"
                    subtitle="Browse available groups and request membership"
                  />
                  <CardContent top="none">
                    <div className="mb-4">
                      <Input
                        id="group-search"
                        placeholder="Search groups..."
                        prefix={<SearchIcon />}
                        value={groupSearch}
                        onChange={(e) => setGroupSearch(e.target.value)}
                      />
                    </div>

                    {availableGroups.length === 0 ? (
                      <p className="text-sm text-color-tertiary text-center py-4">
                        {groupSearch
                          ? 'No groups match your search'
                          : 'No available groups to join'}
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                        {availableGroups.map((group) => (
                          <div
                            key={group.id}
                            className="flex flex-col gap-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-gray-100 dark:bg-gray-700 text-color-secondary dark:text-color-secondary-dark shrink-0">
                                <GroupIcon className="w-4 h-4" />
                              </span>
                              <p className="font-semibold text-sm truncate">
                                {group.name}
                              </p>
                            </div>
                            {group.description && (
                              <p className="text-xs text-color-secondary dark:text-color-secondary-dark line-clamp-2">
                                {group.description}
                              </p>
                            )}
                            <Button
                              variant="secondary"
                              icon={<PlusIcon />}
                              className="mt-auto"
                              disabled={requestJoinMutation.isSubmitting}
                              onClick={() => handleRequestGroup(group.id)}
                            >
                              Request to join
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {activeTab === 'notifications' && (
          <Card className="mt-3">
            <CardHeader
              title="Notification Preferences"
              subtitle="Control how you receive alerts and updates"
            />
            <CardContent top="none">
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Email Notifications</p>
                    <p className="text-xs text-color-tertiary mt-0.5">
                      Receive alerts via email.
                    </p>
                  </div>
                  <Toggle
                    id="notifyEmail"
                    checked={notifyEmail}
                    onChange={() => setNotifyEmail((v) => !v)}
                  />
                </div>
                <Divider />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Browser Push Notifications</p>
                    <p className="text-xs text-color-tertiary mt-0.5">
                      Get real-time push notifications.
                    </p>
                  </div>
                  <Toggle
                    id="notifyBrowser"
                    checked={notifyBrowser}
                    onChange={() => setNotifyBrowser((v) => !v)}
                  />
                </div>
                <Divider />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Incident Alerts</p>
                    <p className="text-xs text-color-tertiary mt-0.5">
                      Notify when services experience issues.
                    </p>
                  </div>
                  <Toggle
                    id="notifyIncidents"
                    checked={notifyIncidents}
                    onChange={() => setNotifyIncidents((v) => !v)}
                  />
                </div>
                <Divider />
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm">Weekly Digest</p>
                    <p className="text-xs text-color-tertiary mt-0.5">
                      Weekly summary of platform activity.
                    </p>
                  </div>
                  <Toggle
                    id="notifyWeekly"
                    checked={notifyWeekly}
                    onChange={() => setNotifyWeekly((v) => !v)}
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <Button variant="primary">Save Preferences</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </Cell>
    </Grid>
  );
};

export default ProfilePage;
